import { useState } from 'react';
import { z } from 'zod';
import { PageSizeSchema, type PageSize } from '#components/patterns/table/types.ts';
import { useBrowserStorage } from '#hooks/useBrowserStorage.ts';

type Props = {
  defaultPageSize: PageSize;
};

/**
 * {@link useSavedPageSize} allows serializing a page size to localStorage and returns the `defaultPageSize` when the value is undefined.
 *
 * It also keeps the value in `useState` in case localStorage is disabled.
 *
 * This value is shared across tables in the app, while all tables use the same key.
 */
export function useSavedPageSize({ defaultPageSize }: Props) {
  const [savedPageSize, setSavedPageSize] = useBrowserStorage<Record<'default', PageSize>>('savedPageSize', 'local');
  const savedPageSizeParseResult = z.object({ default: PageSizeSchema }).safeParse(savedPageSize);
  const [pageSize, setPageSize] = useState<PageSize>(() =>
    savedPageSizeParseResult.success ? savedPageSizeParseResult.data.default : defaultPageSize
  );

  return [
    pageSize,
    (value: PageSize) => {
      setSavedPageSize({ default: value });
      setPageSize(value);
    },
  ] as const;
}
